import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommandButton } from "./components/CommandButton";
import { DockBar } from "./components/DockBar";
import { DockFooter } from "./components/DockFooter";
import { DockPanel } from "./components/DockPanel";
import { HeadsetPicker } from "./components/HeadsetPicker";
import { SettingsRows } from "./components/SettingsRows";
import { ONLINE_THRESHOLD_MS, PREVIEW_ENABLED, SKIP_BEFORE_STEP } from "./constants";
import {
  useDropdowns,
  useFreshToken,
  useHeadsetCommands,
  useHeadsetRealtime,
  useHeadsetSelection,
  useHeadsetSettings,
  useNow,
} from "./hooks";
import { mapWebState } from "./labels";
import { DOCK_ROOT, SCRIM } from "./styles";
import type { HeadsetControlDockProps, HeadsetOption } from "./types";

/**
 * HeadsetControlDock — the "1b status dock" design.
 *
 * Design source: Claude Design project 04d6503a-fa76-45ee-b38e-f4dd7ca9253f,
 * file "Headset Control variants.dc.html", frame 1b.
 *
 * Layout of this module:
 *   types.ts / constants.ts   what the backend sends, and what it does not send yet
 *   styles.ts                 every shared class string, plus the two rules they obey
 *   labels.ts                 scene/step to the wording the design uses
 *   hooks/                    realtime channel, selection, commands, settings, ticker
 *   components/               presentational only; they take values, not the socket
 *   HeadsetControlDock.tsx    this file: wiring and the enable/disable rules
 *
 * This file is deliberately the only one that knows both halves. A component under
 * components/ never talks to the backend, and a hook under hooks/ never emits class
 * names, so either side can be changed without reading the other.
 *
 * Styled with Tailwind only — no companion stylesheet, no inline style objects.
 * Montserrat is already the global body font (src/App.css), so nothing here sets a
 * font family; the mock's font shorthands become plain size/weight utilities.
 *
 * The two widgets that need real behaviour are PrimeReact in `unstyled` mode —
 * Dropdown behind components/Select and InputSwitch behind components/Switch —
 * dressed from styles.ts through their `pt` maps. `unstyled` emits no `p-*` classes,
 * so the globally imported mira theme never reaches them and the Tailwind-only rule
 * still holds: PrimeReact is here for keyboard handling, ARIA and overlay
 * positioning, not for looks. Everything else (the bar, the panel, the pill buttons,
 * the progress strip, the preview) has no PrimeReact counterpart worth having and is
 * plain markup.
 *
 * All user-visible text goes through i18next under the HeadsetDock block of
 * src/translations/main.json. The picker's headset ids, the stimulus names and the
 * language endonyms are values, not copy, and stay untranslated.
 *
 * Replaces HeadsetControlBubble, which is kept in the repo unreferenced as the
 * previous design. All realtime plumbing (negotiate, Web PubSub, heartbeat, vr.state,
 * the 10s presence rule, the preview cache) is carried over from it unchanged — only
 * the surface and the enable/disable rules are new.
 */
export default function HeadsetControlDock({
  apiBaseUrl,
  kc,
  enabled = true,
  showPreview = PREVIEW_ENABLED,
}: HeadsetControlDockProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const now = useNow();
  const getToken = useFreshToken(kc);
  const realtime = useHeadsetRealtime({
    apiBaseUrl,
    getToken,
    enabled,
    acceptPreview: showPreview,
    now,
  });
  const [selectedHeadsetId, setSelectedHeadsetId] = useHeadsetSelection(realtime.headsetIds);
  const sendCommand = useHeadsetCommands({ apiBaseUrl, getToken, headsetId: selectedHeadsetId });

  // Dismissing the dock disarms the reset confirm too: an armed "Reset experience?"
  // that survives a scrim click or an Escape is the one that gets answered by
  // accident on the way back in.
  const collapse = useCallback(() => {
    setExpanded(false);
    setConfirmReset(false);
  }, []);
  const { onListShow, onListHide } = useDropdowns(collapse);

  const { lastSeenByHeadset } = realtime;
  const headsetOptions = useMemo<HeadsetOption[]>(
    () =>
      realtime.headsetIds.map((id) => ({
        id,
        online: now - (lastSeenByHeadset[id] ?? 0) <= ONLINE_THRESHOLD_MS,
      })),
    [realtime.headsetIds, lastSeenByHeadset, now]
  );

  const lastSeen = selectedHeadsetId ? lastSeenByHeadset[selectedHeadsetId] : undefined;
  const selectedOnline = !!lastSeen && now - lastSeen <= ONLINE_THRESHOLD_MS;

  const selectedVr = selectedHeadsetId ? realtime.vrStateByHeadset[selectedHeadsetId] : undefined;

  // ---------------------------------------------------------------------------
  // Enable/disable rules
  // ---------------------------------------------------------------------------
  // Base gate: a headset is selected AND it is beating. Note this differs from the
  // previous design, which enabled every command as soon as any headset had ever
  // been seen; a headset that has gone quiet for 10s now greys its commands.
  const commandsEnabled = !!selectedHeadsetId && selectedOnline;

  const step = selectedOnline ? selectedVr?.step ?? 0 : 0;
  /** A session is running when there is a step. */
  const running = step > 0;

  /**
   * TODO(backend): VrState carries no shortWorkflowEnabled, so this is always false
   * and the third rule below (Skip dead while the short workflow runs) can never fire
   * today. The gate is written out so it starts working the moment the field lands.
   */
  const shortWorkflowOn = false;

  // Start carries no further gate. Neither does Reset experience, which lives in the
  // panel and so is only ever on screen when commandsEnabled already holds.
  const startEnabled = commandsEnabled;
  // Skip is gated three times over: nothing to skip without a step (Scene03 - Idle,
  // and Scene05 with the settings panel open), only the steps before
  // SKIP_BEFORE_STEP may be skipped, and nothing to skip in the short workflow.
  const skipEnabled = commandsEnabled && running && step < SKIP_BEFORE_STEP && !shortWorkflowOn;
  // The stimulus row carries one more rule of its own — Bird may be swapped, Robot
  // may not — but that one reads the switch's own position, so it lives in
  // useHeadsetSettings rather than here.

  // The panel only opens onto a headset that can be driven: one is selected, it is
  // beating, and our own socket is up. Lose any of the three and an open panel
  // collapses (disarming an armed reset confirm with it). The socket is checked on
  // its own even though the whole dock is hidden without it (see the early return
  // below): hiding keeps `expanded` as it was, and it is this effect that clears it,
  // so the dock comes back collapsed after a reconnect. Coming back online does not
  // reopen it.
  //
  // The panel renders on `open`, not on `expanded`: the effect below resets state
  // only after the frame that lost the headset has painted, and that frame must not
  // show the panel. So nothing inside the panel ever sees an offline headset or a
  // dead socket, and it carries no offline variants.
  const canExpand = commandsEnabled && realtime.wsConnected;
  const open = expanded && canExpand;
  useEffect(() => {
    if (!canExpand) collapse();
  }, [canExpand, collapse]);

  const settings = useHeadsetSettings({
    vrState: selectedVr,
    canSend: commandsEnabled,
    sendCommand,
  });

  // Preview frames are opt-in: the headset sends bare heartbeats until asked, since
  // the preview is only looked at while troubleshooting, and the bandwidth is only
  // spent once someone opens the panel. There is no matching stop — the headset
  // keeps streaming until its session ends. Asking again is harmless, so it is
  // asked on every open and on every headset switched to while open, rather than
  // tracking who was already asked: a headset that restarted since is back to bare
  // heartbeats and would never be asked again otherwise. These two handlers are the
  // only ways the panel comes to show a headset — a reconnect never reopens it (see
  // canExpand) — so a new one would have to ask too.
  const toggleExpanded = useCallback(() => {
    if (showPreview && !open && selectedHeadsetId) sendCommand("start_screencast");
    setExpanded((v) => !v);
    setConfirmReset(false);
  }, [showPreview, open, selectedHeadsetId, sendCommand]);

  // Nothing here closes an open combobox: PrimeReact does it itself on an outside
  // click, on scroll and on pick. The new id is passed explicitly because
  // sendCommand still addresses the old selection until the next render. An offline
  // pick collapses the panel instead (see canExpand), so that one is not asked.
  // A switch also disarms the reset confirm: it was armed for the old headset, and
  // left standing it would reset the new one.
  const selectHeadset = useCallback(
    (id: string) => {
      setSelectedHeadsetId(id);
      if (id !== selectedHeadsetId) setConfirmReset(false);
      const online = headsetOptions.some((o) => o.id === id && o.online);
      if (showPreview && open && online && id !== selectedHeadsetId)
        sendCommand("start_screencast", id);
    },
    [showPreview, open, headsetOptions, selectedHeadsetId, setSelectedHeadsetId, sendCommand]
  );

  // Nothing at all is drawn until the backend has accepted us: negotiate is where it
  // checks the license, and a socket is only ever opened on a url it handed out, so
  // a live socket is the proof of both. A refused negotiate (no license) and a
  // dropped connection look the same from here, and both hide the dock; the hooks
  // above keep running regardless, so the socket keeps retrying and the dock
  // reappears — collapsed, see canExpand — once it is back.
  if (!enabled || !realtime.wsConnected) return null;

  const presenceLabel = !selectedHeadsetId
    ? "—"
    : t(selectedOnline ? "HeadsetDock.Online" : "HeadsetDock.Offline");

  return (
    <>
      {open && <div className={SCRIM} onMouseDown={collapse} />}

      <div className={DOCK_ROOT}>
        <DockBar
          online={selectedOnline}
          presenceLabel={presenceLabel}
          webStateLabel={mapWebState(t, selectedVr)}
          step={step}
          // the expanded area owns the full strip, so the bar only shows one while collapsed
          showProgress={running && !open}
          expanded={open}
          canExpand={canExpand}
          onToggleExpanded={toggleExpanded}
          picker={
            <HeadsetPicker
              options={headsetOptions}
              selectedHeadsetId={selectedHeadsetId}
              onSelect={selectHeadset}
              onListShow={onListShow}
              onListHide={onListHide}
            />
          }
          commands={
            <>
              <CommandButton
                size="bar"
                tone={startEnabled ? "primary" : "ghost"}
                disabled={!startEnabled}
                onClick={() => sendCommand("start")}
              >
                {t("HeadsetDock.Start")}
              </CommandButton>
              <CommandButton
                size="bar"
                tone="ghost"
                disabled={!skipEnabled}
                onClick={() => sendCommand("skip")}
              >
                {t("HeadsetDock.Skip")}
              </CommandButton>
            </>
          }
        />

        {open && selectedHeadsetId && (
          <DockPanel
            preview={realtime.previewByHeadset[selectedHeadsetId]?.preview}
            previewPlaceholder={t("HeadsetDock.WaitingForPreview")}
            showPreview={showPreview}
            step={step}
            meta={realtime.metaByHeadset[selectedHeadsetId]}
            settings={<SettingsRows settings={settings} />}
            footer={
              <DockFooter
                confirmReset={confirmReset}
                onAskReset={() => setConfirmReset(true)}
                onCancelReset={() => setConfirmReset(false)}
                onConfirmReset={() => {
                  setConfirmReset(false);
                  sendCommand("reset");
                }}
              />
            }
          />
        )}
      </div>
    </>
  );
}
